// Social Feed Hook
// Main hook for managing social feed state and operations

import { useState, useEffect, useCallback, useRef } from "react";
import { 
  FeedItem, 
  FeedFilters, 
  FeedTab, 
  FeedInteraction, 
  UseSocialFeedReturn,
  DEFAULT_FEED_FILTERS,
  FEED_PAGE_SIZE
} from "../../types/socialFeed";
import { feedService } from "../../services/SocialFeed/feedService";
import {
  Looped,
  LoopCursor,
  startLoop,
  tagCycle,
  appendLooped,
  advanceCursor,
  poolIsEmpty,
} from "../../services/SocialFeed/feedLoop";
import {
  feedCacheKey,
  readFeedCache,
  writeFeedCache,
  clearFeedCache,
} from "../../services/SocialFeed/feedCache";
import { logger } from '../../utils/logger';

/** Ordering seed for one pass through the feed. */
const newFeedSeed = (): string => Math.random().toString(36).slice(2, 10);

export const useSocialFeed = (userId: string): UseSocialFeedReturn => {
  // What this feed last held, if still fresh. Read once at mount so content
  // cannot change under a viewer who is already reading it.
  const [cached] = useState(() =>
    readFeedCache<Looped<FeedItem>>(feedCacheKey('matches', userId))
  );

  const [feedItems, setFeedItems] = useState<Looped<FeedItem>[]>(cached?.items ?? []);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState<FeedFilters>(DEFAULT_FEED_FILTERS);
  const [activeTab, setActiveTab] = useState<FeedTab>("matches");

  // Where the feed has read up to. Held in a ref because the scroll handler
  // that asks for the next page must see the current position, not the one
  // captured when it was last rendered.
  //
  // This used to be a `page` argument defaulting to 0, and every caller invoked
  // loadFeedItems() with no argument — so "load more" re-fetched page one and
  // replaced the list with itself. The feed could not page at all, which is
  // also why it appeared to end after twenty people.
  const cursorRef = useRef<LoopCursor>(cached?.cursor ?? startLoop(newFeedSeed()));

  const loadFeedItems = useCallback(
    async (restart: boolean = false) => {
      if (restart) {
        // An explicit restart is a request for a different feed, so the stored
        // one must not answer it.
        clearFeedCache(feedCacheKey('matches', userId));
        cursorRef.current = startLoop(newFeedSeed());
      }
      const cursor = cursorRef.current;

      setIsLoading(true);
      try {
        const items = await feedService.getFeedItems(userId, {
          page: cursor.offset / FEED_PAGE_SIZE,
          filters,
          tab: activeTab,
          limit: FEED_PAGE_SIZE,
          seed: cursor.seed,
        });

        // Reaching the last candidate starts another pass instead of ending
        // the feed. Dating apps whose feeds bottom out just look empty.
        cursorRef.current = advanceCursor(cursor, items.length, FEED_PAGE_SIZE, newFeedSeed);

        // Only a pass that started at the top and found nobody stops it, which
        // would otherwise spin.
        setHasMore(!poolIsEmpty(cursor, items.length));

        const page = tagCycle(items, cursor.cycle);
        setFeedItems((prev) => (restart ? page : appendLooped(prev, page)));
      } catch (error) {
        console.error("Failed to load feed items:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [userId, filters, activeTab]
  );

  const handleInteraction = useCallback(
    async (interaction: FeedInteraction) => {
      try {
        await feedService.recordInteraction(interaction);
        // Update local state optimistically
        updateFeedItemEngagement(interaction.itemId, interaction.type);
      } catch (error) {
        console.error("Failed to record interaction:", error);
      }
    },
    []
  );

  const updateFeedItemEngagement = (itemId: string, interactionType: string) => {
    setFeedItems((prev) =>
      prev.map((item) => {
        if (item.id === itemId) {
          const updatedEngagement = { ...item.engagement };
          
          switch (interactionType) {
            case "like":
              updatedEngagement.isLiked = !updatedEngagement.isLiked;
              updatedEngagement.likes += updatedEngagement.isLiked ? 1 : -1;
              break;
            case "comment":
              updatedEngagement.comments += 1;
              break;
            case "share":
              updatedEngagement.shares += 1;
              break;
            case "view":
              updatedEngagement.views = (updatedEngagement.views || 0) + 1;
              break;
          }
          
          return { ...item, engagement: updatedEngagement };
        }
        return item;
      })
    );
  };

  const refreshFeed = useCallback(async () => {
    await loadFeedItems(true);
  }, [loadFeedItems]);

  // Hand the feed back for the next visit, pages appended by scrolling
  // included.
  useEffect(() => {
    if (activeTab === 'matches' && feedItems.length) {
      writeFeedCache(feedCacheKey('matches', userId), {
        items: feedItems,
        cursor: cursorRef.current,
      });
    }
  }, [feedItems, userId, activeTab]);

  // Load initial data when userId or activeTab changes
  useEffect(() => {
    if (!userId) return;
    // These items belong to the Matches tab. Loading them while the viewer is
    // reading Explore spends a request on a screen nobody is looking at.
    if (activeTab !== 'matches') return;
    // Already warm, from an earlier visit or from the prefetcher, so there is
    // nothing to wait for and no spinner to show.
    if (feedItems.length) return;
    loadFeedItems(true);
  }, [userId, activeTab]); // Removed loadFeedItems from deps to avoid infinite loop

  return {
    feedItems,
    isLoading,
    hasMore,
    filters,
    activeTab,
    setFilters,
    setActiveTab,
    loadFeedItems,
    handleInteraction,
    refreshFeed,
  };
};
